import { Injectable, inject } from '@angular/core';
import {
  Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppState } from '~core/states/app/app.state';

@Injectable({
  providedIn: 'root'
})
export class LoadingService {

  private appState = inject(AppState);
  private router = inject(Router);

  private excludedParams: string[] = [];
  private excludedRoutes: string[] = [];

  /**
   * Exclude query parameters from triggering loading state
   */
  excludeParams(params: string[]): void {
    this.excludedParams = params;
  }

  /**
   * Exclude route substrings from triggering loading state
   */
  excludeRoutes(routes: string[]): void {
    this.excludedRoutes = routes;
  }

  private shouldShowLoading(event: Event): boolean {
    if (event instanceof NavigationStart) {
      const navigation = this.router.getCurrentNavigation();
      if (!navigation) return true;

      const extras = navigation.extras;

      // Skip if excluded query param is found
      const queryParams = extras?.queryParams || {};
      if (this.excludedParams.some(param => param in queryParams)) {
        return false;
      }

      // Skip if navigation state has skipLoader flag
      if (extras?.state?.skipLoader) {
        return false;
      }

      // Skip if the URL matches an excluded route
      const url = navigation.finalUrl?.toString() || navigation.extractedUrl?.toString();
      if (url && this.excludedRoutes.some(route => url.includes(route))) {
        return false;
      }
    }
    return true;
  }

  init(): void {
    this.router.events
      .pipe(
        filter((event: Event) => event instanceof NavigationStart && this.shouldShowLoading(event))
      )
      .subscribe(() => this.appState.set('loading', true));

    this.router.events
      .pipe(
        filter((event: Event) => (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ))
      )
      .subscribe(() => this.appState.set('loading', false));
  }
}

export const initializeLoading = (loadingSvc: LoadingService) => () => loadingSvc.init();
